import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { SectionIcon } from './sectionIcons';
import type { NavGroup, NavSection } from './types';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideIn = keyframes`from { transform: translateX(-100%); } to { transform: none; }`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  background-color: rgb(17 24 39 / 0.45);
  animation: ${fadeIn} 0.2s ease;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Panel = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: var(--z-modal);
  width: min(85vw, 320px);
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg-elevated);
  box-shadow: var(--shadow-overlay);
  padding-top: env(safe-area-inset-top, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  animation: ${slideIn} 0.22s ease;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px 8px 18px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-primary);
  display: grid;
  place-items: center;
  cursor: pointer;
  &:hover {
    background-color: var(--color-bg-secondary);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0 10px calc(24px + env(safe-area-inset-bottom, 0px));
`;

const GroupName = styled.div`
  padding: 14px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 10px;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-secondary);
  font: inherit;
  font-size: 15px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  svg {
    flex: none;
  }
  &:hover {
    background-color: var(--color-bg-secondary);
    color: var(--color-text-primary);
  }
  &[aria-current='location'] {
    background-color: var(--color-bg-secondary);
    color: var(--color-text-primary);
    font-weight: 700;
    box-shadow: inset 3px 0 0 var(--color-black);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

export interface SectionDrawerProps {
  open: boolean;
  title: string;
  sections: NavSection[];
  groups: NavGroup[];
  activeId: string | null;
  onClose: () => void;
  onSelect: (id: string, viaKeyboard: boolean) => void;
}

/**
 * Spec 030 phone navigation: a panel from the left with every section by group.
 * Escape, the backdrop and the close button dismiss it; Tab stays inside it.
 */
export function SectionDrawer({
  open,
  title,
  sections,
  groups,
  activeId,
  onClose,
  onSelect,
}: SectionDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    // Lock both: the page scrolls inside <body> here, the window elsewhere.
    const locked = [document.documentElement, document.body];
    const previous = locked.map(el => el.style.overflow);
    locked.forEach(el => {
      el.style.overflow = 'hidden';
    });
    const current =
      panelRef.current?.querySelector<HTMLButtonElement>('[aria-current="location"]') ??
      panelRef.current?.querySelector<HTMLButtonElement>('[data-section-item]');
    current?.focus({ preventScroll: true });
    current?.scrollIntoView?.({ block: 'nearest' });
    return () => {
      locked.forEach((el, i) => {
        el.style.overflow = previous[i];
      });
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>('button'));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const titleId = 'section-drawer-title';
  return createPortal(
    <div data-portal-safe="true">
      <Backdrop onClick={onClose} aria-hidden="true" data-testid="section-drawer-backdrop" />
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        data-testid="section-drawer"
      >
        <Head>
          <Title id={titleId}>{title}</Title>
          <IconButton type="button" aria-label="Close sections" onClick={onClose}>
            <SectionIcon name="close" />
          </IconButton>
        </Head>
        <Body>
          {groups.map(group => {
            const items = sections.filter(s => s.group === group.id);
            if (items.length === 0) return null;
            return (
              <div key={group.id} role="group" aria-label={group.label}>
                <GroupName aria-hidden="true">{group.label}</GroupName>
                {items.map(s => (
                  <Item
                    key={s.id}
                    type="button"
                    data-section-item
                    data-testid={`section-drawer-item-${s.id}`}
                    aria-current={activeId === s.id ? 'location' : undefined}
                    onClick={e => onSelect(s.id, e.detail === 0)}
                  >
                    <SectionIcon name={s.icon} />
                    <span>{s.label}</span>
                  </Item>
                ))}
              </div>
            );
          })}
        </Body>
      </Panel>
    </div>,
    document.body,
  );
}
